import React, { forwardRef, useRef } from 'react';

import { AutoComplete } from 'primereact/autocomplete';

/**
 * InputWithSuggestions - Campo de texto con autocompletado/sugerencias.
 *
 * Envuelve PrimeReact AutoComplete y expone la API histórica que usan
 * los formularios de Carta Aval / CreateCaseV3 / EditCaseV3:
 *
 *  - field="label" en AutoComplete para que, al seleccionar una
 *    sugerencia, el input muestre la etiqueta (no el objeto crudo).
 *  - p-inputgroup en el contenedor para conservar el borde y el
 *    aspecto del componente legacy.
 *  - overlayRef forwarded al ref interno de AutoComplete para que
 *    los callers puedan llamar .hide() tras seleccionar.
 *  - onSearchEnter → onKeyDown del AutoComplete.
 *  - Footer opcional "no encontrado" con notFoundBtnLabel /
 *    notFoundBtnClick (cuando la búsqueda devuelve 0 resultados).
 *
 * @param {string} inputName - Nombre e ID del input.
 * @param {string} inputValue - Valor actual del input.
 * @param {function} onTextChange - (e) => void
 * @param {function} onCompleteMethod - (e) => void
 * @param {Array} [suggestions=[]] - Lista de sugerencias.
 * @param {boolean} [inputDisabled=false]
 * @param {string} [inputMode='text']
 * @param {number} [maxLength]
 * @param {function} [itemTemplate]
 * @param {React.Ref} [overlayRef] - Ref al AutoComplete (para .hide()).
 * @param {function} [onSearchEnter] - (e) => void
 * @param {string} [notFoundBtnLabel] - Si se define, muestra botón en
 *   el footer del overlay cuando no hay sugerencias.
 * @param {function} [notFoundBtnClick]
 * @param {string} [className]
 */
const InputWithSuggestions = forwardRef(function InputWithSuggestions(
  {
    inputName,
    inputValue,
    onTextChange,
    onCompleteMethod,
    suggestions = [],
    inputDisabled = false,
    inputMode = 'text',
    maxLength,
    itemTemplate,
    overlayRef,
    onSearchEnter,
    notFoundBtnLabel,
    notFoundBtnClick,
    className = '',
    ...rest
  },
  externalRef
) {
  const localRef = useRef(null);
  const setRef = el => {
    localRef.current = el;
    if (typeof overlayRef === 'function') overlayRef(el);
    else if (overlayRef) overlayRef.current = el;
    if (typeof externalRef === 'function') externalRef(el);
    else if (externalRef) externalRef.current = el;
  };

  const showNotFoundButton =
    !inputDisabled && Array.isArray(suggestions) && suggestions.length === 0 && notFoundBtnLabel;

  return (
    <div className={`p-inputgroup flex w-full ${className}`}>
      <AutoComplete
        ref={setRef}
        id={inputName}
        name={inputName}
        type={inputMode}
        value={inputValue}
        field="label"
        suggestions={suggestions}
        completeMethod={onCompleteMethod}
        onChange={onTextChange}
        onKeyDown={onSearchEnter}
        disabled={inputDisabled}
        maxLength={maxLength}
        itemTemplate={itemTemplate}
        className="w-full"
        panelFooterTemplate={
          showNotFoundButton
            ? () => (
                <button
                  type="button"
                  className="p-autocomplete-not-found-btn"
                  onClick={e => {
                    e.preventDefault();
                    e.stopPropagation();
                    notFoundBtnClick?.();
                  }}
                  style={{
                    width: '100%',
                    padding: '0.5rem 0.75rem',
                    background: 'transparent',
                    border: 'none',
                    color: '#17732E',
                    cursor: 'pointer',
                    fontWeight: 600,
                  }}
                >
                  {notFoundBtnLabel}
                </button>
              )
            : undefined
        }
        {...rest}
      />
    </div>
  );
});

export default InputWithSuggestions;
